import CloseIcon from "@mui/icons-material/Close";
import { Divider, IconButton } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { AxiosError } from "axios";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as constants from "../../constants";
import ContainedButton from "../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../shared/Button/customOutlinedButton";
import CustomTextField from "../../shared/CustomTextFeild";
import ReusableTable from "../../shared/ReusableTable/ReusableTable";
import { setCreateNewInvoice } from "../../store/ProjectInvoices/newProjectInvoiceSlice";
import { RootState } from "../../store/store";
import { getVendorListPId } from "../fiscalUpgrade/services/Invoices/Vendor/VendorListService";
import { notifyUser } from "../fiscalUpgrade/Utils/notifyUser";

type PopUpProps = {
    open: boolean;
    handleClose: () => void;
    popupTitle: string;
    onVendorSelect: (vendorName: string) => void;
    participantId?: number;
};

const vendorColumn = [
    { id: "vendorNumber", label: "Vendor\u00A0#", isHyperlink: false },
    { id: "vendorName", label: "Vendor Name", isHyperlink: false },
    { id: "address", label: "Address", isHyperlink: false },
];

export default function VendorListPopUp({ open, handleClose, popupTitle, participantId, onVendorSelect }: PopUpProps) {

    const dispatch = useDispatch();
    const [listData, setListData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const [vendorName, setVendorName] = useState("");
    const [vendorMessage, setVendorMessage] = useState("");
    const userInfo = useSelector((state: RootState) => state.auth);
    const handleSearch = () => {
        const finalParticipantId = participantId === 0 ? userInfo?.defaultParticipantId : participantId;
        if (finalParticipantId !== undefined) {
            getVendorListByPid(vendorName, finalParticipantId, userInfo?.clientId);
        }
    };

    const handleClear = () => {
        setVendorName("");
        setVendorMessage("");
        setListData([]);
    }

    async function getVendorListByPid(vendorNameSearch: string, pId: number, clientId: number) {
        setIsLoading(true);
        try {
            const resVendorByPid = await getVendorListPId(vendorNameSearch, pId, clientId);
            if (resVendorByPid.status === constants.STATUS_CODES.SUCCESS) {
                if (resVendorByPid.data.data) {
                    setListData(resVendorByPid.data.data);
                } else {
                    setVendorMessage(resVendorByPid.data.message)
                    setListData([]);
                }
            } else {
                setListData([]);
            }
        } catch (error: any) {
            if (error instanceof AxiosError) {
                notifyUser(error.response?.data?.message, "error", translate("Vendor List"));
            } else {
                notifyUser("Unexpected Error:", "error", translate("Vendor List"));
            }
        } finally {
            setIsLoading(false);
        }
    }
    return (
        <Dialog open={open} fullWidth
            maxWidth={false} // Disable predefined widths
            sx={{
                maxWidth: "1000px", // Adjust width as needed
                margin: "auto", // Center horizontally
                display: "flex", // Ensure flex behavior
                alignItems: "center", // Center vertically
                justifyContent: "center", // Center horizontally
                borderRadius: 2,
                "& .MuiPaper-root": { borderRadius: "12px" },
            }}>
            <DialogTitle
                sx={{
                    backgroundColor: "#059ca9",
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '10px 10px',
                    fontWeight: "bold",
                }}
            >
                {popupTitle}
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        color: "white",
                        padding: '0px'
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent style={{ paddingBottom: '0px' }}>
                <div className="search-fields-container">
                    <div className="left-fields">
                        <CustomTextField
                            label={translate("vendorName")}
                            inputProps={{ maxLength: 45 }}
                            value={vendorName}
                            onChange={(e) => setVendorName(e.target.value.trim())}
                            sx={{ width: 200, mt: 1 }}
                        />

                        <ContainedButton
                            sx={{ mt: 1 }} onClick={handleSearch} disabled={!vendorName}>
                            {translate("search")}
                        </ContainedButton>
                        <OutlinedButton
                            sx={{ mt: 1 }} onClick={handleClear} disabled={!vendorName}>
                            {translate("clear")}
                        </OutlinedButton>
                    </div>
                </div>
                <ReusableTable
                    tableKey="VendorList"
                    columns={vendorColumn}
                    rows={listData}
                    loading={isLoading}
                    onRowClick={(row) => {
                        onVendorSelect(row.vendorName);
                        dispatch(setCreateNewInvoice({ vendorNumber: row.vendorNumber }));
                        handleClose();
                    }}
                    vendorMessage={vendorMessage}
                />

            </DialogContent>
            <Divider />
            <DialogActions>
                <OutlinedButton onClick={handleClose}>{translate("cancel")}</OutlinedButton>
            </DialogActions>
        </Dialog>
    );
}
